import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "./firebase";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  serverTimestamp,
  getDocs,
  updateDoc,
  arrayUnion
} from "firebase/firestore";
import { MathfieldElement } from "mathlive";
import StudentLayout from "./StudentLayout";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import calculatorImg from "./assets/calculator.png";
import noCalculatorImg from "./assets/no-calculator.png";

const StudentAssignmentViewer = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [done, setDone] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [questionResults, setQuestionResults] = useState([]);
  const [studentAnswers, setStudentAnswers] = useState([]);
  const [workingAnswers, setWorkingAnswers] = useState([]);
  const [working, setWorking] = useState("");
  const [reviewMode, setReviewMode] = useState(false);

  useEffect(() => {
    if (!customElements.get("math-field")) {
      customElements.define("math-field", MathfieldElement);
    }
  }, []);

  useEffect(() => {
    const fetchAssignment = async () => {
      const assignmentRef = doc(db, "assignments", assignmentId);
      const assignmentSnap = await getDoc(assignmentRef);
      const assignmentData = assignmentSnap.data();
      const user = auth.currentUser;
if (!user) return;
      if (assignmentData.activityType === "millionaire") {
        navigate(`/millionaire/${assignmentId}`);
        return;
      }
      
      if (assignmentData.activityType === "blockbusters") {
        const user = auth.currentUser;
        if (!user) return;
      
        navigate(`/blockbusters-welcome/${assignmentId}?studentUID=${user.uid}`);
        return;
      }
      setAssignment(assignmentData);
      setAssignment(assignmentData);

      // 🆕 Create submission document for this student
      const submissionId = `${user.uid}_${assignmentId}`;
      const submissionRef = doc(db, "studentAssignmentSubmissions", submissionId);
      const submissionSnap = await getDoc(submissionRef);
      
      if (!submissionSnap.exists()) {
        await setDoc(submissionRef, {
          assignmentId,
          studentUID: user.uid,
          type: "self-marked-quiz",
          timeStarted: serverTimestamp(),
                    timeSubmitted: null,
                    answers: [],
        });
        console.log("📝 Submission doc created for self-marked quiz");
      }
      
      

      const questionDocs = await Promise.all(
        assignmentData.questions.map((qid) =>
          getDoc(doc(db, "questions", qid))
        )
      );

      const fetchedQuestions = questionDocs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setQuestions(fetchedQuestions);
      const len = fetchedQuestions.length;
      setQuestionResults(Array(len).fill(null));
      setStudentAnswers(Array(len).fill(""));
      setWorkingAnswers(Array(len).fill(""));

      await loadProgress(fetchedQuestions.length);
    };

    fetchAssignment();
  }, [assignmentId]);
  const loadProgress = async (questionCount) => {
    const user = auth.currentUser;
    if (!user) return;

    const progressRef = doc(db, "assignmentProgress", `${assignmentId}_${user.uid}`);
    const snap = await getDoc(progressRef);
    if (!snap.exists()) return;

    const data = snap.data();
    setCurrent(data.current || 0);
    setStudentAnswers(data.studentAnswers || Array(questionCount).fill(""));
    setWorkingAnswers(data.workingAnswers || Array(questionCount).fill(""));
    setQuestionResults(data.questionResults || Array(questionCount).fill(null));
    setAttempts(data.attempts || 0);

    await fetchResponses(user.uid, questionCount);
  };

  const fetchResponses = async (userId) => {
    const responsesRef = collection(db, "responses");
    const querySnapshot = await getDocs(responsesRef);
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.studentUID === userId && data.assignmentId === assignmentId) {
        setAttempts(data.attempts || 0);
      }
    });
  };

  const saveProgress = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const progressRef = doc(db, "assignmentProgress", `${assignmentId}_${user.uid}`);
    await setDoc(progressRef, {
      assignmentId,
      studentUID: user.uid,
      current,
      studentAnswers,
      workingAnswers,
      questionResults,
      attempts,
      updatedAt: serverTimestamp(),
    });
  };

  const logResponseToFirebase = async (isCorrect) => {
    const user = auth.currentUser;
    if (!user) return;

    const responseRef = doc(db, "responses", `${assignmentId}_${user.uid}_${current}`);
    const responseSnap = await getDoc(responseRef);
    const response = responseSnap.exists() ? responseSnap.data() : {};
    const attemptsMade = response.attempts || 0;

    await setDoc(responseRef, {
      assignmentId,
      studentUID: user.uid,
      questionIndex: current,
      answer: studentAnswer,
      working,
      isCorrect,
      attempts: attemptsMade + 1,
      timestamp: serverTimestamp(),
    });
  };
  const logAnswerToSubmission = async (isCorrect) => {
    const user = auth.currentUser;
    if (!user) return;
  
    const submissionId = `${user.uid}_${assignmentId}`;
    const submissionRef = doc(db, "studentAssignmentSubmissions", submissionId);
  
    const answerObject = {
      correct: isCorrect,
      playerAnswer: studentAnswer,
      working,
      questionId: questions[current]?.id || "",
      timeStarted: new Date(),
      timeSubmitted: new Date(),
    };
  
    try {
      await updateDoc(submissionRef, {
        answers: arrayUnion(answerObject),
      });
      console.log("📥 Answer logged to submission");
    } catch (err) {
      console.error("❌ Failed to log answer to submission:", err);
    }
  };
  
  const checkAnswer = async () => {
    const q = questions[current];
    const correctAnswers = q.answers?.length ? q.answers : [q.answer || ""];
    const trimmedInput = studentAnswer.trim().replace(/\s+/g, "");

    const isCorrect = correctAnswers.some(
      (ans) => trimmedInput === ans.trim().replace(/\s+/g, "")
    );

    const updatedStudentAnswers = [...studentAnswers];
    const updatedWorkingAnswers = [...workingAnswers];
    updatedStudentAnswers[current] = studentAnswer;
    updatedWorkingAnswers[current] = working;

    setStudentAnswers(updatedStudentAnswers);
    setWorkingAnswers(updatedWorkingAnswers);

    await logResponseToFirebase(isCorrect);
    await logAnswerToSubmission(isCorrect); // 🆕 Add to studentAssignmentSubmissions


    if (isCorrect) {
      // Play correct answer sound
      const audio = new Audio("/self-marked/correct.mp3");
      audio.play();
    
      setFeedback("✅ Correct!");
      updateResult("correct");
    }
     else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= 3) {
        // Play wrong answer sound
        const audio = new Audio("/self-marked/wrong.mp3");
        audio.play().catch(err => console.warn("Failed to play sound:", err));
      
        updateResult("incorrect");
        const firstAnswer = correctAnswers[0] || "—";
        setFeedback(
          <div>
            {"❌ Not quite. Correct answer: "}
            <BlockMath math={firstAnswer} />
          </div>
        );
      }
       else {
        setFeedback(`❌ Try again. Attempts left: ${3 - newAttempts}`);
      }
    }

    await saveProgress();
  };

  const updateResult = (result) => {
    const updated = [...questionResults];
    updated[current] = result;
    setQuestionResults(updated);
  };
  const nextQuestion = async () => {
    setFeedback(null);
    setStudentAnswer("");
    setWorking("");
    setAttempts(0);
    if (current + 1 < questions.length) {
      setCurrent((prev) => prev + 1);
    } else {
      setDone(true);
      await markAssignmentComplete();
    }
    await saveProgress();
  };

  const prevQuestion = async () => {
    if (current > 0) {
      setCurrent(current - 1);
      setFeedback(null);
      setAttempts(0);
      setStudentAnswer(studentAnswers[current - 1] || "");
      setWorking(workingAnswers[current - 1] || "");
    }
    await saveProgress();
  };

  const markAssignmentComplete = async () => {
    const user = auth.currentUser;
    if (!user) return;
  
    // ✅ 1. Update "assignments" collection (homework marked complete)
    const assignmentRef = doc(db, "assignments", assignmentId);
    await setDoc(assignmentRef, { completed: true }, { merge: true });
  
    // ✅ 2. Update "studentAssignmentSubmissions" collection to set timeSubmitted
    const submissionId = `${user.uid}_${assignmentId}`;
    const submissionRef = doc(db, "studentAssignmentSubmissions", submissionId);
  
    await updateDoc(submissionRef, {
      timeSubmitted: serverTimestamp(),
    });
  
    console.log("✅ timeSubmitted updated in studentAssignmentSubmissions!");
  };

  const handleReview = () => {
    setCurrent(0);
    setReviewMode(true);
  };

  const finishReview = () => {
    if (assignment.assignmentType === "homework") {
      navigate("/homework-page");
    } else {
      navigate("/classwork-page");
    }
  };

  const feedbackMessage = (score, total) => {
    const percentage = (score / total) * 100;
    if (percentage === 100) return "🌟 Perfect! You got everything right!";
    if (percentage >= 80) return "🎉 Great job! You did really well!";
    if (percentage >= 50) return "👍 Good effort! Keep practising!";
    return "💪 Don't worry! Review your answers and try again!";
  };

  // Treat string paragraphs as LaTeX unless they are image URLs.
// Also handle stringified JSON paragraphs.
const renderParagraphs = (paragraphs) => (
  paragraphs.map((raw, i) => {
    let p = raw;

    // If Firestore stored a JSON string, try to parse it
    if (typeof p === "string") {
      try {
        const maybeObj = JSON.parse(p);
        if (maybeObj && (maybeObj.text || maybeObj.image)) {
          p = maybeObj;
        }
      } catch {
        // keep as string
      }
    }

    // If it's still a string: image URL or LaTeX text
    if (typeof p === "string") {
  const isImg = /^https?:\/\/.+\.(png|jpe?g|webp|gif|svg)$/i.test(p);
  if (isImg) {
    return (
      <p key={i}>
        <img
          src={p}
          alt={`Paragraph Image ${i + 1}`}
          style={{ maxWidth: "100%", maxHeight: 300, display: "block", margin: "0.5rem auto" }}
        />
      </p>
    );
  }

  // Detect LaTeX markers (starts with $...$ or contains \frac, \sqrt, etc.)
  const isLatex = /\\[a-zA-Z]+|\\frac|\\sqrt|\$.*\$/.test(p);

  if (isLatex) {
    return (
      <div
        key={i}
        style={{ display: "flex", justifyContent: "center", margin: "0.5rem 0" }}
      >
        <BlockMath math={p.replace(/^\$|\$$/g, "")} />
      </div>
    );
  }

    // Otherwise: plain text
  return (
    <p key={i} style={{ textAlign: "left" }}>
      {p}
    </p>
  );
}

// If it's an object with text/image
if (p && typeof p === "object" && (p.text || p.image)) {
  return (
    <div key={i} style={{ marginBottom: "1rem" }}>
      {p.text && <BlockMath>{p.text}</BlockMath>}
      {p.image && (
        <img
          src={p.image}
          alt={`Paragraph Image ${i + 1}`}
          style={{
            maxWidth: "100%",
            maxHeight: 300,
            display: "block",
            margin: "0.5rem auto",
          }}
        />
      )}
    </div>
  );
}


    // Fallback
    return <p key={i}>{String(p)}</p>;
  })
);

  const renderProgress = () => (
  <div style={styles.progressWrapper}>
    {questionResults.map((res, index) => {
      let bgColor = "#636e72";
      if (res === "correct") bgColor = "#00b894";
      if (res === "incorrect") bgColor = "#d63031";

      return (
        <div
          key={index}
          style={{
            ...styles.progressDot,
            backgroundColor: bgColor,
            border: index === current ? "3px solid #f9b710" : "2px solid transparent",
          }}
        >
          <span style={styles.dotNumber}>{index + 1}</span>
        </div>
      );
    })}
  </div>
);

  if (!assignment || questions.length === 0) {
    return (
      <StudentLayout>
        <div style={styles.container}>Loading assignment...</div>
      </StudentLayout>
    );
  }

  const question = questions[current];
  const correctAnswers = question.answers?.length ? question.answers : [question.answer || ""];
  const canAdvance = feedback?.toString().startsWith("✅") || attempts >= 3;
  const score = questionResults.filter((r) => r === "correct").length;

  return (
    <StudentLayout>
      <div style={styles.container}>
        <h1 style={styles.heading}>📝 {assignment.title}</h1>
        {renderProgress()}
        {reviewMode && (
          <button style={styles.finishReviewBtn} onClick={finishReview}>
            🔙 Finish Review
          </button>
        )}

        {done && !reviewMode ? (
          <div style={styles.resultsBox}>
            <h2>📊 Score: {score} / {questions.length}</h2>
            <p style={styles.feedbackText}>{feedbackMessage(score, questions.length)}</p>
            <button onClick={handleReview} style={styles.reviewBtn}>🔍 Review Questions</button>
          </div>
        ) : (
          <div style={styles.card}>
            <h3>Question {current + 1} of {questions.length}</h3>
            {question.calculator === "yes" ? (
              <img src={calculatorImg} alt="Calculator Allowed" style={{ height: 50, marginBottom: 10 }} />
            ) : (
              <img src={noCalculatorImg} alt="No Calculator" style={{ height: 50, marginBottom: 10 }} />
            )}

            {question.questionParagraphs ? (
              renderParagraphs(question.questionParagraphs)
            ) : (
              <>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.6", textAlign: "left" }}>
  {/\$|\\[a-zA-Z]+/.test(question.questionText) ? (
    <BlockMath math={question.questionText.replace(/^\$|\$$/g, "")} />
  ) : (
    <p>{question.questionText}</p>
  )}
</div>




                {question.questionLatex && (
  <div className="wrap-math">
    <BlockMath math={question.questionLatex} />
  </div>
)}

              </>
            )}

            {question.optionalQuestionText && <p>{question.optionalQuestionText}</p>}

            <label style={{ fontWeight: "bold", marginTop: "5px", display: "inline-block" }}>
              🧠 Show your working:
            </label>
            <textarea
              rows={4}
              readOnly={reviewMode || questionResults[current] === "correct" || attempts >= 3}
              value={reviewMode ? workingAnswers[current] : working}
              onChange={(e) => !reviewMode && setWorking(e.target.value)}
              placeholder="Show optional working here"
              style={{
                ...styles.workingInput,
                backgroundColor: reviewMode || questionResults[current] === "correct" || attempts >= 3 ? "#f0f0f0" : "#fff",
              }}
            />

            <div style={{ margin: "1rem 0" }}>
              <label><strong>Answer:</strong></label>
              <div style={{ display: "flex", alignItems: "center" }}>
                {question.answerPrefix && <BlockMath math={question.answerPrefix} />}
                <math-field
  readOnly={reviewMode || questionResults[current] === "correct" || attempts >= 3}
  style={{
    ...styles.mathInput,
    backgroundColor:
      reviewMode || questionResults[current] === "correct" || attempts >= 3
        ? "#f0f0f0"
        : "#fff",
  }}
  value={
    reviewMode || questionResults[current] === "correct" || attempts >= 3
      ? studentAnswers[current]
      : studentAnswer
  }  
  onInput={(e) => !reviewMode && setStudentAnswer(e.target.value)}
/>

               {question.answerSuffix && (
  <div style={{ marginLeft: '0.75rem' }}>
    <BlockMath math={question.answerSuffix} />
  </div>
)}
              </div>
            </div>

            {reviewMode && (
              <div style={{ marginBottom: "1rem" }}>
                <p><strong>{questionResults[current] === "correct" ? "✅ Correct Answer:" : "❌ The correct answer was:"}</strong></p>
                <BlockMath math={correctAnswers[0]} />
                {question.explanation && <p><strong>🧠 Explanation:</strong> {question.explanation}</p>}
              </div>
            )}

            {!reviewMode && feedback && <div style={{ marginBottom: "1rem" }}>{feedback}</div>}

            {!reviewMode && !canAdvance && (
              <button onClick={checkAnswer} style={styles.submitBtn}>Check Answer</button>
            )}
            {(canAdvance || reviewMode) && (
              <>
                {reviewMode && current > 0 && (
                  <button onClick={prevQuestion} style={styles.backBtn}>← Back</button>
                )}
                <button onClick={nextQuestion} style={styles.nextBtn}>
                  {current + 1 < questions.length ? "Next ➡️" : reviewMode ? "Finish" : "Finish 🎉"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "'Fredoka', cursive",
    backgroundColor: "#eaf4fc",
    minHeight: "100vh",
  },
  heading: {
    fontSize: "2rem",
    marginBottom: "1rem",
  },
  card: {
    backgroundColor: "#fff",
    padding: "2rem",
    borderRadius: "12px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    maxWidth: "700px",
    margin: "1rem auto 0",
  },
  mathInput: {
    flex: 1,
    fontSize: "1.2rem",
    padding: "0.5rem",
    borderRadius: "8px",
    border: "1px solid #ccc",
    minWidth: "200px",
    marginLeft: "5px",
    backgroundColor: "#fff",
    transition: "background-color 0.3s ease",
  },
  suffix: {
    fontSize: "1.2rem",
    marginLeft: "0.75rem",
  },
  submitBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#0984e3",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginRight: "1rem",
  },
  nextBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#00b894",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  backBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#00b894",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginRight: "1rem",
  },
  progressWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
  },
  progressDot: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    color: "white",
    transition: "all 0.3s ease",
  },
  dotNumber: {
    userSelect: "none",
  },
  resultsBox: {
    textAlign: "center",
    marginTop: "3rem",
  },
  feedbackText: {
    fontSize: "1.25rem",
    margin: "1rem 0",
  },
  reviewBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#636e72",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  finishReviewBtn: {
    display: "block",
    margin: "0 auto 1.5rem",
    padding: "0.5rem 1rem",
    fontWeight: "bold",
    backgroundColor: "#fdcb6e",
    color: "#2d3436",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  workingInput: {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #ccc",
    padding: "0.75rem",
    fontSize: "1rem",
    fontFamily: "'Fredoka', cursive",
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
    backgroundColor: "#fff",
    transition: "background-color 0.3s ease",
  },
  questionLatex: {
  textAlign: "left",
  fontSize: "1rem", // match Fredoka text size
  fontFamily: "'Fredoka', cursive",
  marginTop: "1rem",
},

};

export default StudentAssignmentViewer;
